import { ActionIcon, Box, Flex, createStyles, useMantineTheme } from '@mantine/core';
import Link from 'next/link';

import { LogoTypes, SplashLogo } from '~/components/shared/Logos/SplashLogo';
import { Text } from '~/domains/design-system/Text';
import IconX from '~/domains/design-system/icons/IconX';
import { TruncateText } from '~/domains/design-system/TruncateText';
import { LogoSplashTextShort } from '~/domains/common/components/AppShellHeader/components/Header/assets/LogoSplashTextShort';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { Routes } from '~/domains/common/constants/routes';
import isWebView from '~/utils/isWebView';

const useStyles = createStyles((theme) => ({
  container: {
    background: theme.black,
    zIndex: theme.other.zIndex.header,
    height: 72,
    width: '100%',

    '&.webView': {
      background: theme.white,
    },
  },
  content: {
    height: 72,
    maxWidth: theme.other.layout.maxWidth,
    paddingLeft: theme.other.layout.paddingLg,
    paddingRight: theme.other.layout.paddingLg,
    margin: '0 auto',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.other.spacing._40,

    [theme.fn.smallerThan('lg')]: {
      gap: /* eslint-disable-line local-rules/use-theme-tokens */ 20,
      paddingLeft: theme.other.layout.paddingMd,
      paddingRight: theme.other.layout.paddingMd,
    },
    [theme.fn.smallerThan('md')]: {
      paddingLeft: theme.other.layout.paddingSm,
      paddingRight: theme.other.layout.paddingSm,
    },
  },

  headline: {
    flex: 2,
  },
  closeButton: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  logo: {
    flex: 1,
    justifyContent: 'flex-start',
  },
}));

export function SectionHeader({
  title,
  contestName,
  closeUrl = Routes.home(),
}: {
  title?: string;
  contestName?: string;
  closeUrl?: string | (() => void);
}) {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const theme = useMantineTheme();
  const webView = isWebView();

  if (webView) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Flex className={classes.logo} gap={theme.other.spacing._4}>
          <SplashLogo type={LogoTypes.PRIMARY_NO_TEXT} size={20} />
          {!isMobile ? <LogoSplashTextShort color={theme.white} /> : null}
        </Flex>

        <Flex direction="column" align="center" className={classes.headline}>
          {title ? (
            <Text
              color={theme.white}
              variant={{ base: 'headline-large', sm: 'headline-xl' }}
              ta="center"
            >
              {title}
            </Text>
          ) : null}
          {contestName ? (
            <Box ta="center" w={{ base: 200, sm: 600 }}>
              <TruncateText
                tooltipLabel={contestName}
                variant="subhead-medium"
                c={theme.colors.gray2[3]}
              >
                {contestName}
              </TruncateText>
            </Box>
          ) : null}
        </Flex>
        <Flex className={classes.closeButton} justify="end">
          {typeof closeUrl === 'string' ? (
            <ActionIcon variant="transparent" radius="xl" component={Link} href={closeUrl}>
              <IconX color={theme.white} />
            </ActionIcon>
          ) : (
            <ActionIcon variant="transparent" radius="xl" onClick={closeUrl}>
              <IconX color={theme.white} />
            </ActionIcon>
          )}
        </Flex>
      </div>
    </div>
  );
}
